import React from 'react';
import noop from 'utils/noop';
import Cell from './Cell';
import { CHECKBOX_SIZE, ROW_PADDING } from './helpers';
import { RowContainer } from './Row.styled';
import { shouldComponentUpdate } from './Row.utils';

// type Props = {
//   /** An additional table row class. */
//   className: string,
//   /** The component to use for the table cell in a row. */
//   CellComponent: React.Component,
//   /** An additional table cell class. */
//   cellClassName: string,
//   /** Array of cell data to create row. If the cell object does not contain a "value" the cell will not render */
//   cells: [],
//   /** The columns of the table - used to see if rows need to get updated in the case of toggling customized columns
//    * See Table.js for additional details.
//    */
//   columns: [],
//   /** If previous column is expanded and should take over the space of entire row. */
//   expandColumn: ?boolean,
//   /** Highlights row on hover. */
//   hoverable: boolean,
//   /** If row is indeterminate. */
//   indeterminate: boolean,
//   /**
//    * Callback function when row is double clicked.
//    *
//    * ```
//    * onDoubleClickRow(e, rowProps) => {}
//    * ```
//    * @param { Object } e: The double click event.
//    * @param { Object } rowData: The data object of the row.
//    * @returns { void }
//    */
//   onDoubleClickRow: (e: {}, rowData: {}) => void,
//   /**
//    * Callback function when row selected state changes
//    *
//    * ```
//    * onSelectRow(e, rowProps) => {}
//    * ```
//    * @param { Object } e: The click event.
//    * @param { Object } rowProps: Props of the row including selected state of row.
//    * @returns { void }
//    */
//   onSelectRow: (e: {}, rowProps: {}) => void,
//   /** Content to display over the row. */
//   overlayContent: node,
//   /** Data role attribute. */
//   role: string,
//   /** Data of the row */
//   rowData: {},
//   /** If row is selected. */
//   selected: boolean,
//   /** If row is selectable. */
//   selectable: boolean,
//   /** The value of the row. */
//   value: *,
// };

export default class Row extends React.Component {
  static defaultProps = {
    CellComponent: Cell,
    columns: [],
    cells: [],
    data: {},
    hoverable: true,
    onDoubleClickRow: noop,
    onSelectRow: noop,
    role: 'row',
    selected: false,
    selectable: false,
  };

  handleDoubleClick = (e) => {
    const { rowData, onDoubleClickRow } = this.props;
    onDoubleClickRow(e, rowData);
  };

  shouldComponentUpdate = (nextProps) => shouldComponentUpdate(nextProps, this.props);

  /**
   * Callback function when row is selected.
   *
   * ```
   * handleSelectRow(e, checkboxProps)
   * ```
   * @param { Object } e: The change event.
   * @param { Object } rowProps: The props of the selcted row checkbox containing checked value.
   * @returns { void }
   */
  handleSelectRow = (e, rowProps) => {
    const { onSelectRow } = this.props;
    const { checked } = rowProps;
    const newProps = {
      ...this.props,
      selected: checked,
    };
    onSelectRow(e, newProps);
  };

  /**
   * Creates a row in the table.
   *
   * ```
   * generateRow()
   * ```
   * @returns { React.Component } The generated row.
   */
  generateRow = () => {
    const {
      CellComponent,
      cellClassName,
      cells,
      expandColumn,
      indeterminate,
      selected,
      selectable,
      value,
      ...otherProps
    } = this.props;

    /**
     * Adds a checkbox cell if row is selectable.
     */
    const selectedCell = {
      checked: selected,
      indeterminate,
      onClick: this.handleSelectRow,
      type: 'checkbox',
      value,
      maxWidth: CHECKBOX_SIZE,
    };
    let minWidth = 0;
    const row = selectable ? [selectedCell, ...cells] : [...cells];
    const rowCells = row.map((cellData, key) => {
      const { minWidth: minCellWidth = 0 } = cellData;
      minWidth += minCellWidth + ROW_PADDING;
      if (!expandColumn || (expandColumn && cellData.value)) {
        return (
          <CellComponent
            key={`cell-${key}`}
            index={key}
            {...cellData}
            {...otherProps}
            className={cellClassName}
          />
        );
      }
      return null;
    });
    return { cells: rowCells, style: { minWidth } };
  };

  render() {
    const { className, hoverable, role, selected, selectable, overlayContent } = this.props;
    const { cells, style } = this.generateRow();
    return (
      <RowContainer
        className={`${className} tableRow`}
        data-role={role}
        data-selected={selectable ? selected : undefined}
        data-hoverable={hoverable}
        data-has-overlay={!!overlayContent}
        onDoubleClick={this.handleDoubleClick}
        style={style}
        data-test="tableRow"
      >
        {cells}
        {overlayContent}
      </RowContainer>
    );
  }
}
